import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import Gender from '../../components/customLayout/gender';
import { birthDate2age, countGender } from '../../utils/utils';
import { useTournamentStore } from '../../store/tournamentStore';
import { useGlobalStore } from '../../store/globalStore';
import { formatDate } from '../../utils/utils';
import axiosInstance from '../../axiosConfig';

const TournamentPlayersTable: React.FC = () => {
  const activePlayers = useTournamentStore((state) => state.activePlayers);
  const isLoading = useGlobalStore((state) => state.isLoading);

  const removeActivePlayer = useTournamentStore(
    (state) => state.removeActivePlayer,
  );

  const [pendingDelete, setPendingDelete] = useState<{ id: string, countdown: number } | null>(null);
  const [undoTimer, setUndoTimer] = useState<NodeJS.Timeout | null>(null);

  const handleDeletePlayer = (playerId: string, id: string) => {
    const playerToRestore = activePlayers.find((p) => String(p.memberNumber) === String(playerId));
    setPendingDelete({ id: playerId, countdown: 3 });
    let count = 3;
    const timer = setInterval(async () => {
      count--;
      setPendingDelete((prev) => prev && prev.id === playerId ? { ...prev, countdown: count } : prev);
      if (count === 0) {
        clearInterval(timer);
        setPendingDelete(null);
        try {
          await axiosInstance.delete('/tournament/player', { params: { player_id: id } });
          removeActivePlayer(playerId);
        } catch (e) {
          console.log(e);
          if (playerToRestore) {
            alert('Der Spieler konnte aufgrund eines Serverfehlers nicht gelöscht werden und wurde wieder zur Liste hinzugefügt.');
            useTournamentStore.getState().addActivePlayer(playerToRestore);
          }
        }
      }
    }, 1000);
    setUndoTimer(timer);
  };

  const handleUndo = () => {
    if (undoTimer) clearInterval(undoTimer);
    setPendingDelete(null);
  };

  const tableHeader = [
    { key: 'number', text: '#️⃣' },
    { key: 'name', text: '👤 Player' },
    { key: 'hcp', text: '🏌️ HCP' },
    { key: 'gender', text: '⚧️ Geschlecht' },
    { key: 'memberNumber', text: '🆔 Mitgliedsnummer' },
    { key: 'birthDate', text: '🎂 Geburtsdatum' },
    { key: 'age', text: '🎂 Alter' },
    { key: 'action', text: '⚙️ Aktion' },
  ];

  return (
    <div className="bg-white relative overflow-scroll p-2 border rounded-md">
      {isLoading && (
        <div className="absolute flex items-center justify-center left-0 top-0 w-full z-10 backdrop-blur-sm h-full">
          <div className="text-xl animate-pulse">Bitte warten ...</div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {tableHeader.map((header) => (
              <TableHead className="whitespace-nowrap" key={header.key}>
                {header.text}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {activePlayers.length === 0 && (
            <TableRow>
              <TableCell className="py-10" colSpan={tableHeader.length}>
                <div className="text-center py-10">
                  <div className="text-2xl">Keine Spieler gefunden</div>
                  <div>Es wurden keine Spieler in der Liste gefunden.</div>
                </div>
              </TableCell>
            </TableRow>
          )}

          {activePlayers.map((cell, index) => (
            <TableRow
              key={index}
              className={`animate__animated animate__fadeInLeft whitespace-nowrap`}
            >
              <TableCell>
                <div className="font-medium text-center">{index + 1}</div>
              </TableCell>
              <TableCell className="grid gap-1">
                <div className="font-medium flex gap-1">
                  <b>
                    {cell.title} {cell.lastName},
                  </b>{' '}
                  {cell.name}
                </div>
              </TableCell>
              <TableCell>
                <div>
                  {cell.pending && '⚠️ '}
                  {cell.hcp ? cell.hcp.toString().replace('.', ',') : '0,0'}
                  <div style={{ fontSize: '0.6rem', color: 'gray' + '' }}>
                    {cell.pending && 'Nicht aktualisiert'}
                  </div>
                </div>
              </TableCell>
              <TableCell className="flex gap-2 items-center">
                <Gender gender={cell.gender as 'M' | 'W'} />
                {cell.gender === 'M' ? 'Männlich' : 'Weiblich'}
              </TableCell>
              <TableCell>{cell.memberNumber}</TableCell>
              <TableCell>
                {cell.birthDate ? formatDate(cell.birthDate) : '?'}
              </TableCell>
              <TableCell>
                <div
                  className={`text-center ${birthDate2age(cell.birthDate) <= 18 && 'font-bold border border-orange-200 rounded-full bg-orange-50'}`}
                >
                  {cell.birthDate ? birthDate2age(cell.birthDate) : '?'}
                </div>
              </TableCell>
              <TableCell>
                {pendingDelete && pendingDelete.id === cell.memberNumber ? (
                  <div
                    onClick={handleUndo}
                    className="text-orange-500 text-xs font-medium cursor-pointer"
                  >
                    {`Rückgängig ${pendingDelete.countdown}s`}
                  </div>
                ) : (
                  <div
                    onClick={() => handleDeletePlayer(String(cell.memberNumber), String(cell.id))}
                    className="text-red-500 font-medium cursor-pointer"
                  >
                    löschen
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}

          {activePlayers.length !== 0 && (
            <TableRow>
              <TableCell></TableCell>
              <TableCell>
                <div className="text-xs font-medium">
                  <div>♂️ {countGender(activePlayers, 'M')} Männlich</div>
                  <div>♀️ {countGender(activePlayers, 'W')} Weiblich</div>
                </div>
              </TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default TournamentPlayersTable;
